import { computed, Injectable, signal } from '@angular/core';
import { updatePrimaryPalette } from '@primeng/themes';

export type AppTheme = 'standard' | 'dark' | 'light' | 'system';
export type EffectiveTheme = Exclude<AppTheme, 'system'>;
export type AppPaletteStyle = 'vivid' | 'matte';
export type AppPalette =
  | 'indigo'
  | 'blue'
  | 'emerald'
  | 'teal'
  | 'amber'
  | 'orange'
  | 'pink'
  | 'purple'
  | 'red'
  | 'burgundy'
  | 'brown'
  | 'olive'
  | 'navy'
  | 'slate';

// The inline script in `src/index.html` mirrors this key and the `theme-*` body classes.
const STORAGE_KEY = 'app-theme';
const APP_THEMES: AppTheme[] = ['standard', 'dark', 'light', 'system'];
const PALETTE_STORAGE_KEY = 'app-palette';
const PALETTE_STYLE_STORAGE_KEY = 'app-palette-style';
const PALETTE_SHADES = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950];
// Keep in sync with `$matte-amount` in `_palettes.scss`.
export const MATTE_AMOUNT = '60%';
export const APP_PALETTES: AppPalette[] = [
  'indigo',
  'blue',
  'emerald',
  'teal',
  'amber',
  'orange',
  'pink',
  'purple',
  'red',
  'burgundy',
  'brown',
  'olive',
  'navy',
  'slate',
];
// Deep tones Aura lacks; the preset's `extend` turns them into `--p-<name>-*` and `{<name>.*}` tokens.
export const CUSTOM_PALETTE_SCALES = {
  burgundy: {
    50: '#fff4f5',
    100: '#fde4e7',
    200: '#faccd1',
    300: '#eaa0aa',
    400: '#b15063',
    500: '#8e1b3d',
    600: '#78072e',
    700: '#630024',
    800: '#4e001a',
    900: '#3d0013',
    950: '#2a000a',
  },
  brown: {
    50: '#faf6f3',
    100: '#f3e9e2',
    200: '#e9d5c8',
    300: '#d0b09b',
    400: '#a07659',
    500: '#7d4e2c',
    600: '#6a3e1e',
    700: '#583217',
    800: '#47270f',
    900: '#3a1f0b',
    950: '#291507',
  },
  olive: {
    50: '#f6f8f1',
    100: '#eaeddf',
    200: '#d6ddc1',
    300: '#b3be8e',
    400: '#8a9753',
    500: '#647119',
    600: '#535f06',
    700: '#465103',
    800: '#3a4202',
    900: '#303703',
    950: '#242a06',
  },
  navy: {
    50: '#f3f7fe',
    100: '#e3ebfb',
    200: '#cadaf8',
    300: '#9eb7ea',
    400: '#4b6aac',
    500: '#22428d',
    600: '#153277',
    700: '#0d2561',
    800: '#061a4c',
    900: '#03113b',
    950: '#010826',
  },
};

export function loadStoredPalette(): AppPalette {
  const stored = localStorage.getItem(PALETTE_STORAGE_KEY);
  return APP_PALETTES.find((name) => name === stored) ?? 'indigo';
}

export function loadStoredPaletteStyle(): AppPaletteStyle {
  return localStorage.getItem(PALETTE_STYLE_STORAGE_KEY) === 'matte' ? 'matte' : 'vivid';
}

// Primary scale as preset tokens; matte greys each shade with the slate shade of the same step.
export function primaryPaletteScale(name: AppPalette, style: AppPaletteStyle): Record<number, string> {
  const scale = PALETTE_SHADES.map((shade) => [
    shade,
    style === 'vivid'
      ? `{${name}.${shade}}`
      : `color-mix(in srgb, {${name}.${shade}} ${MATTE_AMOUNT}, {slate.${shade}})`,
  ]);
  return Object.fromEntries(scale);
}

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly darkSchemeQuery = window.matchMedia('(prefers-color-scheme: dark)');
  private readonly systemPrefersDark = signal(this.darkSchemeQuery.matches);
  readonly currentTheme = signal<AppTheme>(this.loadTheme());
  readonly effectiveTheme = computed<EffectiveTheme>(() => {
    const theme = this.currentTheme();
    if (theme !== 'system') {
      return theme;
    }
    return this.systemPrefersDark() ? 'dark' : 'light';
  });
  private readonly selectedPalette = signal<AppPalette>(loadStoredPalette());
  readonly currentPalette = this.selectedPalette.asReadonly();
  private readonly selectedPaletteStyle = signal<AppPaletteStyle>(loadStoredPaletteStyle());
  readonly currentPaletteStyle = this.selectedPaletteStyle.asReadonly();

  // The stored primary palette is already in `JRetailPreset` (`app.config.ts`), so startup needs no update.
  constructor() {
    this.applyTheme();
    this.applyPaletteClass();
    this.darkSchemeQuery.addEventListener('change', (event) => {
      this.systemPrefersDark.set(event.matches);
      this.applyTheme();
    });
  }

  setTheme(theme: AppTheme): void {
    this.currentTheme.set(theme);
    localStorage.setItem(STORAGE_KEY, theme);
    this.applyTheme();
  }

  setPalette(name: AppPalette): void {
    this.selectedPalette.set(name);
    localStorage.setItem(PALETTE_STORAGE_KEY, name);
    this.applyPaletteClass();
    this.applyPrimaryPalette();
  }

  setPaletteStyle(style: AppPaletteStyle): void {
    this.selectedPaletteStyle.set(style);
    localStorage.setItem(PALETTE_STYLE_STORAGE_KEY, style);
    this.applyPaletteClass();
    this.applyPrimaryPalette();
  }

  private loadTheme(): AppTheme {
    const stored = localStorage.getItem(STORAGE_KEY);
    return APP_THEMES.find((theme) => theme === stored) ?? 'standard';
  }

  private applyTheme(): void {
    const theme = this.effectiveTheme();
    document.body.classList.toggle('theme-dark', theme === 'dark');
    document.body.classList.toggle('theme-light', theme === 'light');
  }

  private applyPaletteClass(): void {
    const body = document.body;
    APP_PALETTES.forEach((paletteName) => body.classList.remove(`palette-${paletteName}`));
    body.classList.add(`palette-${this.selectedPalette()}`);
    body.classList.toggle('palette-matte', this.selectedPaletteStyle() === 'matte');
  }

  private applyPrimaryPalette(): void {
    updatePrimaryPalette(primaryPaletteScale(this.selectedPalette(), this.selectedPaletteStyle()));
  }
}
